import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import emptySelectOption from '@/constants/emptySelectOption';
import useBookingParams from '@/hooks/useBookingParams';
import Alert from '@/ui/components/Alert';
import Card from '@/ui/components/Card';
import Checkbox from '@/ui/components/Form/Checkbox';
import Label from '@/ui/components/Form/Label';
import Select from '@/ui/components/Form/Select';
import Toggle from '@/ui/components/Form/Toggle';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import { replaceTimezone } from '@/utils/dates';

import PersonContext from '../Person/PersonContext';
import { PaymentMethod } from '../Profile/components/PaymentRow/types';

import { buildConstraints, getConstraintBannerText } from './bookingConstraints';
import { Slots } from './Slots';
import { StaffIndicators } from './StaffIndicators';
import { SlotBookingProps, StaffOption } from './types';

const PROVIDER_FILTER_ALL = 'all';
const PROVIDER_FILTER_CRED = 'cred';

type PROVIDER_FILTER_TYPE = typeof PROVIDER_FILTER_ALL | typeof PROVIDER_FILTER_CRED;

const PROVIDER_FILTER_OPTIONS = [
  { label: 'Credentialed Only', value: PROVIDER_FILTER_CRED },
  { label: 'All Available', value: PROVIDER_FILTER_ALL }
];

export const SlotBooking = ({
  appointmentType,
  appointmentTypeHealthieId,
  timeZone
}: SlotBookingProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    person: { time_zone, id: personId, healthie_id, payment_type, jurisdiction_id }
  } = useContext(PersonContext);

  const isSelfPay = payment_type === PaymentMethod.SELF_PAY;

  const [initialSelectedProviders, setInitialSelectedProviders] = useState<string[]>(() => {
    return searchParams.getAll('providerIds') ?? [];
  });
  const [initialSelectedStaffUUIDs, setInitialSelectedStaffUUIDs] = useState<string[]>(() => {
    return searchParams.getAll('staffId') ?? [];
  });
  const [calculatedEndDate, setCalculatedEndDate] = useState<boolean>(true);
  const [showExtraSlots, setShowExtraSlots] = useState<boolean>(false);

  const constraints = buildConstraints(searchParams);

  const [providersFilter, setProvidersFilter] =
    useState<PROVIDER_FILTER_TYPE>(PROVIDER_FILTER_CRED);

  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(() => {
    const now = new Date();
    const sevenDaysLater = new Date(now);
    sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
    sevenDaysLater.setHours(24, 0, 0, 0);
    if (time_zone) return replaceTimezone(sevenDaysLater, time_zone || 'UTC');
    return sevenDaysLater;
  });

  const canLoadBookingParams = !!(startDate && endDate && appointmentTypeHealthieId && personId);

  const {
    data: bookingParamsData,
    isPending: loadingBookingParams,
    isError: errorLoadingBookingParams
  } = useBookingParams(
    {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      appointment_type_id: appointmentTypeHealthieId,
      person_id: personId
    },
    {
      enabled: canLoadBookingParams
    }
  );

  const availableStaff = useMemo(() => {
    const data = bookingParamsData?.data?.staff || [];

    if (
      bookingParamsData?.data?.most_recent_staff &&
      !data.some((staff) => staff.id === bookingParamsData?.data?.most_recent_staff?.id)
    ) {
      data.push(bookingParamsData.data.most_recent_staff);
    }

    const filteredData =
      bookingParamsData?.data?.staff.filter((s) =>
        providersFilter === PROVIDER_FILTER_CRED ? s.is_credentialed : true
      ) || [];

    const enchancedData = filteredData.map((staff) => {
      if (bookingParamsData?.data?.most_recent_staff?.id === staff.id) {
        return { ...staff, is_most_recent: true };
      }
      return { ...staff, is_most_recent: false };
    });

    return enchancedData as StaffOption[];
  }, [bookingParamsData, providersFilter]);

  const [selectedProviders, setSelectedProviders] = useState<StaffOption[]>(() => {
    const providerIdsParams = searchParams.getAll('providerIds');
    const selectedStaff = availableStaff.filter((staff) => providerIdsParams.includes(staff.id));
    return selectedStaff;
  });

  const handleAvailableStaffSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setSelectedProviders(availableStaff);
    } else {
      setSelectedProviders([]);
    }
  };

  useEffect(() => {
    // if no credentialed providers exist in the response, fall back to showing all
    if (providersFilter === PROVIDER_FILTER_CRED && bookingParamsData?.data?.staff) {
      const hasCredentialed = bookingParamsData.data.staff.some((s) => s.is_credentialed);
      if (!hasCredentialed) {
        setProvidersFilter(PROVIDER_FILTER_ALL);
      }
    }
  }, [bookingParamsData, providersFilter]);

  useEffect(() => {
    // updates start and end date when time zone changes
    if (timeZone && calculatedEndDate) {
      const newEndDate = new Date(startDate);
      newEndDate.setDate(newEndDate.getDate() + 7);
      newEndDate.setHours(24, 0, 0, 0);
      setEndDate(replaceTimezone(newEndDate, timeZone));
    }
  }, [timeZone, startDate, calculatedEndDate]);

  useEffect(() => {
    // runs on first load. sets dates from url params if present
    // or sets url params if not present
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');

    const urlParams = new URLSearchParams(searchParams.toString());
    const now = new Date();

    if (startDateParam) {
      const parsedDate = new Date(startDateParam);
      if (!isNaN(parsedDate.getTime())) {
        // Use the later of the parsed date or now to avoid showing past dates
        const effectiveStartDate = parsedDate > now ? parsedDate : now;
        setStartDate(effectiveStartDate);
      }
    } else if (constraints) {
      const target = new Date(constraints.targetDate + 'T00:00:00');
      if (constraints.targetConstraint === 'after') {
        const dayAfter = new Date(target);
        dayAfter.setDate(dayAfter.getDate() + 1);
        setStartDate(dayAfter > now ? dayAfter : now);
      } else if (constraints.targetConstraint === 'on') {
        setStartDate(target > now ? target : now);
      }
      // 'before': leave startDate as now
    } else {
      urlParams.set('startDate', startDate.toISOString());
    }

    if (endDateParam) {
      const parsedDate = new Date(endDateParam);
      if (!isNaN(parsedDate.getTime())) {
        // if a date parameter is present, changing the time zone has a different effect
        // than if the end date is calculated based on the start date.
        setCalculatedEndDate(false);
        setEndDate(parsedDate);
      }
    } else if (constraints) {
      const target = new Date(constraints.targetDate + 'T00:00:00');
      if (constraints.targetConstraint === 'on') {
        const dayAfter = new Date(target);
        dayAfter.setDate(dayAfter.getDate() + 1);
        setEndDate(dayAfter);
        setCalculatedEndDate(false);
      } else if (constraints.targetConstraint === 'before') {
        // target midnight is the exclusive upper bound — includes all slots up to end of the day before
        setEndDate(target < endDate ? target : endDate);
        setCalculatedEndDate(false);
      }
      // 'after': leave endDate calculated as usual
    } else {
      urlParams.set('endDate', endDate.toISOString());
    }

    if (!startDateParam || !endDateParam) {
      setSearchParams(urlParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resets that used to run from the wrapper's Controller onChange handlers --
  // now the wrapper only owns the form value, so these run as effects keyed on
  // the resolved props instead. Skip mount and the empty -> value hydration from
  // a deep link so the URL's providers and date range aren't clobbered.
  const previousAppointmentTypeHealthieId = useRef(appointmentTypeHealthieId);
  useEffect(() => {
    const previous = previousAppointmentTypeHealthieId.current;
    previousAppointmentTypeHealthieId.current = appointmentTypeHealthieId;
    if (!previous || previous === appointmentTypeHealthieId) return;

    setSelectedProviders([]);
    setCalculatedEndDate(true);
    setProvidersFilter(PROVIDER_FILTER_CRED);
    setStartDate(new Date());
  }, [appointmentTypeHealthieId]);

  const isFirstTimeZoneRender = useRef(true);
  useEffect(() => {
    if (isFirstTimeZoneRender.current) {
      isFirstTimeZoneRender.current = false;
      return;
    }
    setSelectedProviders([]);
    setProvidersFilter(PROVIDER_FILTER_CRED);
  }, [timeZone]);

  useEffect(() => {
    // sets provider ids in the URL when they change
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('providerIds');
        selectedProviders.forEach((provider) => {
          if (provider.healthie_id) params.append('providerIds', provider.healthie_id.toString());
        });
        return params;
      },
      { replace: true }
    );
  }, [selectedProviders, setSearchParams]);

  useEffect(() => {
    const hasHealthieIds = initialSelectedProviders.length > 0;
    const hasUUIDs = initialSelectedStaffUUIDs.length > 0;

    if (availableStaff.length > 0 && (hasHealthieIds || hasUUIDs)) {
      const selectedStaff = availableStaff.filter(
        (staff) =>
          (hasHealthieIds && initialSelectedProviders.includes(staff.healthie_id.toString())) ||
          (hasUUIDs && initialSelectedStaffUUIDs.includes(staff.id))
      );
      if (selectedStaff.length > 0) {
        setSelectedProviders(selectedStaff);
        setInitialSelectedProviders([]);
        setInitialSelectedStaffUUIDs([]);
      }
    }
  }, [availableStaff, initialSelectedProviders, initialSelectedStaffUUIDs]);

  if (!healthie_id) {
    return (
      <div className="px-4">
        <Alert level="error">Member doesn&apos;t have a healthie account</Alert>
      </div>
    );
  }

  if (!jurisdiction_id) {
    return (
      <div className="px-4">
        <Alert level="error">Member doesn&apos;t have a jurisdiction</Alert>
      </div>
    );
  }

  return (
    <>
      <div className="mt-6">
        <Heading
          value={3}
          className="mb-3">{`Selected Providers (${selectedProviders.length})`}</Heading>
        {loadingBookingParams && canLoadBookingParams ? (
          <Spinner label="Loading available staff..." />
        ) : errorLoadingBookingParams ? (
          <Alert level="error">Failed to load available staff</Alert>
        ) : (
          <Card className="mb-4">
            <div className="mb-4">
              <Select
                label=""
                options={PROVIDER_FILTER_OPTIONS}
                selectedOption={
                  PROVIDER_FILTER_OPTIONS.find((option) => option.value === providersFilter) ??
                  emptySelectOption
                }
                onChange={(option) => setProvidersFilter(option.value as PROVIDER_FILTER_TYPE)}
              />
            </div>

            {!isSelfPay &&
              bookingParamsData?.data?.credentialing_errors &&
              bookingParamsData.data.credentialing_errors.length > 0 &&
              bookingParamsData.data.credentialing_errors.map((error, index) => (
                <div key={index} className="mb-2">
                  <Alert key={index} level="error" dismissable={false}>
                    {error}
                  </Alert>
                </div>
              ))}

            {availableStaff.length === 0 ? (
              <Alert level="info" dismissable={false}>
                No staff available for this member in the next seven days
              </Alert>
            ) : (
              <>
                <Label
                  className="flex items-center text-underline"
                  htmlFor="action-provider-select-all"
                  reverse
                  text="Select all">
                  <input
                    className="mr-1"
                    id="action-provider-select-all"
                    onChange={handleAvailableStaffSelectAll}
                    type="checkbox"
                  />
                </Label>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {availableStaff.map((staff) => {
                    const healthieId = staff.healthie_id;
                    if (!healthieId) return null;

                    return (
                      <div key={staff.id} className="flex items-center">
                        <span id={`staff-${staff.id}`} className="flex flex-row items-center">
                          <Checkbox
                            id={`staff-${staff.id}`}
                            checked={selectedProviders.includes(staff)}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setSelectedProviders([...selectedProviders, staff]);
                              } else {
                                setSelectedProviders(
                                  selectedProviders.filter((selected) => selected.id !== staff.id)
                                );
                              }
                            }}
                            label={`${staff.first_name} ${staff.last_name || ''}`.trim()}
                          />
                          <StaffIndicators
                            isCredentialed={staff.is_credentialed}
                            isSelfPay={isSelfPay}
                            credentialingLinkTo={`/credentialing?staff_id=${staff.id}&jurisdiction_id=${bookingParamsData?.data?.credentialing_critera?.jurisdiction_id}&provider_id=${bookingParamsData?.data?.credentialing_critera?.insurance_provider_id}&insurance_type=${bookingParamsData?.data?.credentialing_critera?.insurance_type}&program_scope=${bookingParamsData?.data?.credentialing_critera?.program_scope}`}
                            hasConflictOfInterest={staff.has_conflict_of_interest}
                            isMostRecent={staff.is_most_recent}
                          />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        )}
      </div>

      {constraints && (
        <div className="mt-4">
          <Alert level="info" dismissable={false}>
            <div className="flex items-center justify-between gap-4">
              <span>{getConstraintBannerText(constraints)}</span>
              {constraints.targetMode === 'approximate' && (
                <Toggle
                  enabled={showExtraSlots}
                  onChange={setShowExtraSlots}
                  label="Show all times"
                />
              )}
            </div>
          </Alert>
        </div>
      )}
      <div className="mt-4">
        <Slots
          providers={selectedProviders}
          appointmentType={appointmentType}
          startDate={startDate}
          endDate={endDate}
          timeZone={timeZone}
          blockedTimes={bookingParamsData?.data?.blocked_times || []}
          appointmentLocationIds={bookingParamsData?.data?.appointment_location_ids || []}
          appointmentDurationMinutes={bookingParamsData?.data?.appointment_type_duration_minutes}
          jurisdictionId={jurisdiction_id}
          allowLoadMore={calculatedEndDate}
          constraints={constraints ?? undefined}
          showExtraSlots={showExtraSlots}
        />
      </div>
    </>
  );
};
